/**
 * custom-variant-info.js
 * CCC custom feature — shows SKU, barcode, weight and bin location
 * for the selected variant on product pages.
 *
 * Listens to Horizon's native variant events from events.js.
 * Reads full variant data from the embedded JSON data island
 * (because the event payload only exposes id/sku/available).
 */

import { ThemeEvents } from '@theme/events';

class CccVariantInfo extends HTMLElement {
  connectedCallback() {
    // Parse the embedded variant data island
    const dataEl = this.querySelector('[data-variant-info-json]');
    if (!dataEl) return;

    try {
      this._variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[CCC] custom-variant-info: failed to parse variant data', e);
      return;
    }

    // Subscribe to both events:
    // - variant:selected updates immediately from the local data island.
    // - variant:update confirms the state after Horizon's section render finishes.
    this._boundSelectedHandler = this._onVariantSelected.bind(this);
    this._boundHandler = this._onVariantUpdate.bind(this);
    document.addEventListener(ThemeEvents.variantSelected, this._boundSelectedHandler);
    document.addEventListener(ThemeEvents.variantUpdate, this._boundHandler);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantSelected, this._boundSelectedHandler);
    document.removeEventListener(ThemeEvents.variantUpdate, this._boundHandler);
  }

  _onVariantSelected(event) {
    if (!this._isSameProductContext(event)) return;

    const variantId = event.detail?.resource?.variantId;
    if (variantId) this._updateDisplay(variantId);
  }

  _onVariantUpdate(event) {
    // Same-product guard (matching _onVariantSelected): quick-add dialogs on
    // this page fire variant:update for OTHER products, whose ids are never
    // in this island — without the guard every such change warned to console.
    if (!this._isSameProductContext(event)) return;

    const resource = event.detail?.resource;
    if (!resource?.id) return;
    // setTimeout(0): run after any potential DOM morph finishes (defensive)
    setTimeout(() => this._updateDisplay(resource.id), 0);
  }

  _updateDisplay(variantId) {
    const variant = this._variants?.find((v) => String(v.id) === String(variantId));
    if (!variant) {
      console.warn('[CCC] variant-info: variant not found in data island', variantId);
      return;
    }

    this._set('[data-cvi-sku]', variant.sku || 'N/A');
    this._set('[data-cvi-barcode]', this._displayBarcode(variant.barcode));
    this._set('[data-cvi-weight]', variant.weight > 0 ? `${variant.weight}g` : 'N/A');
    // Kits assembled from parts carry the parts' locations instead of a bin.
    this._set('[data-cvi-bin]', variant.bin_location || variant.kit_locations || 'N/A');

    // Hide rows where value is N/A and the block has hideEmpty enabled
    if (this.dataset.hideEmpty === 'true') {
      this.querySelectorAll('.cvi-row').forEach((row) => {
        const val = row.querySelector(
          '[data-cvi-sku],[data-cvi-barcode],[data-cvi-weight],[data-cvi-dimensions],[data-cvi-bin]'
        );
        if (val) {
          const shown = (val.querySelector('.ccc-copy__value')?.textContent || val.textContent || '').trim();
          row.hidden = shown === 'N/A';
        }
      });
    }

    // Re-apply copy wrappers if variant update wiped them back to plain text
    document.dispatchEvent(new CustomEvent('ccc:variant-info-updated', { bubbles: true }));
  }

  /**
   * Staff sometimes store barcode "NO" when there is no barcode.
   * Treat blank / "NO" the same as missing → N/A.
   * @param {unknown} value
   * @returns {string}
   */
  _displayBarcode(value) {
    const trimmed = String(value ?? '').trim();

    if (!trimmed || trimmed.toLowerCase() === 'no') {
      return 'N/A';
    }

    return trimmed;
  }

  _set(selector, value) {
    const el = this.querySelector(selector);
    if (!el) return;

    const copyHost = el.classList.contains('ccc-copy')
      ? el
      : el.querySelector('.ccc-copy');

    if (copyHost instanceof HTMLElement) {
      const valueEl = copyHost.querySelector('.ccc-copy__value');
      if (valueEl) valueEl.textContent = value;
      else copyHost.textContent = value;

      if (!value || value === 'N/A') {
        // Unwrap back to plain text so empty rows can hide cleanly
        el.textContent = value;
        return;
      }

      copyHost.dataset.cccCopy = value;
      const label = (copyHost.getAttribute('aria-label') || 'Copy value')
        .replace(/^Copy\s+/i, '')
        .replace(/^Copied\s+/i, '');
      copyHost.setAttribute('aria-label', `Copy ${label}`);
      return;
    }

    el.textContent = value;
  }

  _isSameProductContext(event) {
    if (!(event.target instanceof HTMLElement)) return false;

    const sourceSection = event.target.closest('.shopify-section, dialog');
    const hostSection = this.closest('.shopify-section, dialog');

    return !sourceSection || !hostSection || sourceSection === hostSection;
  }
}

if (!customElements.get('ccc-variant-info')) {
  customElements.define('ccc-variant-info', CccVariantInfo);
}
